/**
 * @file TicketsPage.tsx
 * @brief Central support queue. Any user can log a ticket against a project.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Plus, Ticket as TicketIcon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { ticketService, type Ticket, type TicketStatus } from '@/services/api/ticketService';
import { enumMasterService, userService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import { useAppDispatch } from '@/store';
import { openModal } from '@/store/slices/uiSlice';

function errorText(err: unknown, fallback: string): string {
  const error = err as { response?: { data?: { message?: string } }; message?: string };
  return error.response?.data?.message || error.message || fallback;
}

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  task_created: 'Task created',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const STATUS_COLOR: Record<TicketStatus, 'warning' | 'info' | 'success' | 'default'> = {
  open: 'warning',
  task_created: 'info',
  closed: 'success',
  cancelled: 'default',
};

const FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'task_created', label: 'Task created' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_FILTERS = [
  { value: '', label: 'Any priority' },
  { value: 'p1', label: 'P1' },
  { value: 'p2', label: 'P2' },
  { value: 'p3', label: 'P3' },
  { value: 'p4', label: 'P4' },
];

const RESOLVE_FILTERS = [
  { value: '', label: 'Any resolve date' },
  { value: 'due_by_today', label: 'Due by today' },
  { value: 'today', label: 'Due today' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'upcoming', label: 'Upcoming' },
];

function dueByToday(ticket: Ticket): boolean {
  return ticket.status === 'open' && Boolean(ticket.resolveBy) && ticket.resolveBy! <= dayjs().format('YYYY-MM-DD');
}

const TicketsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFilter = searchParams.get('projectId') ?? '';
  const openId = searchParams.get('open') ?? '';
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [resolve, setResolve] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState(projectFilter);
  const [ticketPriority, setTicketPriority] = useState('p3');
  const [resolveBy, setResolveBy] = useState(dayjs().format('YYYY-MM-DD'));
  const [dueDate, setDueDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [category, setCategory] = useState('');
  const [estimate, setEstimate] = useState('60');
  const [responsibleUserId, setResponsibleUserId] = useState('');
  const [resolution, setResolution] = useState('');
  const [showConvert, setShowConvert] = useState(false);
  const [showClose, setShowClose] = useState(false);

  const filters = {
    status: status || undefined,
    projectId: projectFilter || undefined,
    search: search.trim() || undefined,
    priority: priority || undefined,
    resolve: resolve || undefined,
  };

  const ticketsQ = useQuery({
    queryKey: QUERY_KEYS.tickets(filters),
    queryFn: () => ticketService.list(filters),
  });
  const projectsQ = useQuery({
    queryKey: QUERY_KEYS.ticketProjects,
    queryFn: () => ticketService.listProjects(),
  });
  const categoriesQ = useQuery({
    queryKey: QUERY_KEYS.taskCategories(),
    queryFn: () => enumMasterService.listTaskCategories(),
    enabled: showConvert,
  });
  const usersQ = useQuery({
    queryKey: ['users', 'raci', selected?.projectId],
    queryFn: () => userService.getRACIAssignableUsers(selected?.projectId),
    enabled: Boolean(selected?.projectId && showConvert),
  });

  useEffect(() => {
    if (!openId || !ticketsQ.data) return;
    const match = ticketsQ.data.items.find((item) => item.id === openId);
    if (match) setSelected(match);
  }, [openId, ticketsQ.data]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tickets'] });
  };

  const createMutation = useMutation({
    mutationFn: () => ticketService.create({
      projectId,
      title: title.trim(),
      description: description.trim(),
      priority: ticketPriority,
      resolveBy,
    }),
    onSuccess: async () => {
      showSuccess('Ticket logged');
      setCreateOpen(false);
      setTitle('');
      setDescription('');
      await refresh();
    },
    onError: (err: unknown) => showError(errorText(err, 'Could not log the ticket')),
  });

  const convertMutation = useMutation({
    mutationFn: () => ticketService.convert(selected!.id, {
      startDate,
      dueDate,
      category,
      estimatedMinutes: Number(estimate),
      responsibleUserId: responsibleUserId || undefined,
    }),
    onSuccess: async (ticket) => {
      showSuccess('Ticket converted to a task');
      setSelected(ticket);
      setShowConvert(false);
      await refresh();
    },
    onError: (err: unknown) => showError(errorText(err, 'Could not convert the ticket')),
  });

  const closeMutation = useMutation({
    mutationFn: () => ticketService.close(selected!.id, resolution.trim()),
    onSuccess: async (ticket) => {
      showSuccess('Ticket closed');
      setSelected(ticket);
      setShowClose(false);
      setResolution('');
      await refresh();
    },
    onError: (err: unknown) => showError(errorText(err, 'Could not close the ticket')),
  });

  const cancelMutation = useMutation({
    mutationFn: () => ticketService.cancel(selected!.id),
    onSuccess: async (ticket) => {
      showSuccess('Ticket cancelled');
      setSelected(ticket);
      await refresh();
    },
    onError: (err: unknown) => showError(errorText(err, 'Could not cancel the ticket')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => ticketService.remove(selected!.id),
    onSuccess: async () => {
      showSuccess('Ticket deleted');
      setSelected(null);
      await refresh();
    },
    onError: (err: unknown) => showError(errorText(err, 'Could not delete the ticket')),
  });

  const items = ticketsQ.data?.items ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {FILTERS.map((filter) => (
            <Chip
              key={filter.value || 'all'}
              label={filter.label}
              color={status === filter.value ? 'primary' : 'default'}
              variant={status === filter.value ? 'filled' : 'outlined'}
              onClick={() => setStatus(filter.value)}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ minWidth: 280 }}>
          <AppSelect label="Priority" value={priority} onChange={(event) => setPriority(String(event.target.value))} formControlProps={{ sx: { minWidth: 140 } }}>
            {PRIORITY_FILTERS.map((item) => <AppMenuItem key={item.value || 'any'} value={item.value}>{item.label}</AppMenuItem>)}
          </AppSelect>
          <AppSelect label="Resolve by" value={resolve} onChange={(event) => setResolve(String(event.target.value))} formControlProps={{ sx: { minWidth: 180 } }}>
            {RESOLVE_FILTERS.map((item) => <AppMenuItem key={item.value || 'any-date'} value={item.value}>{item.label}</AppMenuItem>)}
          </AppSelect>
        </Stack>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => { setProjectId(projectFilter); setCreateOpen(true); }}>
          Log ticket
        </Button>
      </Box>

      <TextField
        size="small"
        placeholder="Search tickets"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ maxWidth: 360 }}
      />

      {projectFilter ? (
        <Chip
          label={`Project: ${projectsQ.data?.find((project) => project.id === projectFilter)?.name ?? 'Selected project'}`}
          onDelete={() => {
            searchParams.delete('projectId');
            setSearchParams(searchParams);
          }}
        />
      ) : null}

      {ticketsQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : ticketsQ.isError ? (
        <Typography color="error">{(ticketsQ.error as Error).message}</Typography>
      ) : items.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <TicketIcon size={28} />
          <Typography sx={{ mt: 1 }} color="text.secondary">No tickets yet</Typography>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {items.map((ticket) => (
            <Card
              key={ticket.id}
              sx={{ p: 2, cursor: 'pointer', borderColor: dueByToday(ticket) ? 'error.main' : undefined, borderWidth: dueByToday(ticket) ? 1 : undefined, borderStyle: 'solid' }}
              onClick={() => {
                setSelected(ticket);
                setShowConvert(false);
                setShowClose(false);
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{ticket.ticketRef}</Typography>
                  <Typography fontWeight={600}>{ticket.title}</Typography>
                  <Typography variant="body2" color={dueByToday(ticket) ? 'error' : 'text.secondary'}>
                    {ticket.priority.toUpperCase()} · Resolve by {ticket.resolveBy ? dayjs(ticket.resolveBy).format('D MMM YYYY') : '—'}
                    {dueByToday(ticket) ? ' · Due' : ''}
                    {' · '}{ticket.projectName || 'No project'}
                  </Typography>
                </Box>
                <Chip size="small" label={STATUS_LABEL[ticket.status]} color={STATUS_COLOR[ticket.status]} />
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log a ticket</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <AppSelect label="Project" value={projectId} onChange={(event) => setProjectId(String(event.target.value))}>
            {(projectsQ.data ?? []).map((project) => (
              <AppMenuItem key={project.id} value={project.id}>
                {project.projRef ? `${project.projRef} · ` : ''}{project.name}
              </AppMenuItem>
            ))}
          </AppSelect>
          <TextField label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
          <TextField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={3} />
          <AppSelect label="Priority" value={ticketPriority} onChange={(event) => setTicketPriority(String(event.target.value))}>
            {PRIORITY_FILTERS.filter((item) => item.value).map((item) => (
              <AppMenuItem key={item.value} value={item.value}>{item.label}</AppMenuItem>
            ))}
          </AppSelect>
          <TextField label="Resolve by" type="date" value={resolveBy} onChange={(event) => setResolveBy(event.target.value)} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!projectId || !title.trim() || !description.trim() || !resolveBy || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Log ticket
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        {selected ? (
          <>
            <DialogTitle>
              {selected.ticketRef}
              <Typography variant="body2" color="text.secondary">{selected.projectName}</Typography>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography fontWeight={600}>{selected.title}</Typography>
                <Chip size="small" label={STATUS_LABEL[selected.status]} color={STATUS_COLOR[selected.status]} />
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.description}</Typography>
              <Typography variant="body2" color={dueByToday(selected) ? 'error' : 'text.secondary'}>
                {selected.priority.toUpperCase()} · Resolve by {selected.resolveBy ? dayjs(selected.resolveBy).format('D MMM YYYY') : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">Logged by {selected.createdByName || 'Unknown'}</Typography>
              {selected.taskId ? (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelected(null);
                      dispatch(openModal({ type: 'taskDetail', data: { taskId: selected.taskId } }));
                    }}
                  >
                    Open task {selected.taskRef || ''}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Delete that task before this ticket can be deleted. After the task is deleted, you can create a task from this ticket again.
                  </Typography>
                </>
              ) : null}
              {selected.resolution ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">Resolution</Typography>
                  <Typography variant="body2">{selected.resolution}</Typography>
                </Box>
              ) : null}
              {showConvert ? (
                <Stack spacing={2}>
                  <AppSelect label="Category" value={category} onChange={(event) => setCategory(String(event.target.value))}>
                    {(categoriesQ.data ?? []).map((item) => (
                      <AppMenuItem key={item.id} value={item.keyValue}>{item.displayLabel}</AppMenuItem>
                    ))}
                  </AppSelect>
                  <TextField label="Start date" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }} />
                  <TextField label="Due date" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} InputLabelProps={{ shrink: true }} />
                  <TextField label="Estimated time (minutes)" type="number" value={estimate} onChange={(event) => setEstimate(event.target.value)} />
                  <AppSelect label="Responsible" value={responsibleUserId} onChange={(event) => setResponsibleUserId(String(event.target.value))}>
                    {(usersQ.data ?? []).map((person) => (
                      <AppMenuItem key={person.id} value={person.id}>
                        {person.displayName || person.firstName || person.email}
                      </AppMenuItem>
                    ))}
                  </AppSelect>
                </Stack>
              ) : null}
              {showClose ? (
                <TextField label="Resolution" value={resolution} onChange={(event) => setResolution(event.target.value)} multiline minRows={3} />
              ) : null}
            </DialogContent>
            <DialogActions>
              {selected.canManage && selected.status === 'open' && !showConvert ? (
                <Button onClick={() => setShowConvert(true)}>Convert to task</Button>
              ) : null}
              {showConvert ? (
                <Button
                  variant="contained"
                  disabled={!startDate || !dueDate || !category || Number(estimate) <= 0 || convertMutation.isPending}
                  onClick={() => convertMutation.mutate()}
                >
                  Create task
                </Button>
              ) : null}
              {selected.canManage && selected.status === 'task_created' && !showClose ? (
                <Button onClick={() => setShowClose(true)}>Close</Button>
              ) : null}
              {showClose ? (
                <Button variant="contained" disabled={!resolution.trim() || closeMutation.isPending} onClick={() => closeMutation.mutate()}>
                  Save resolution
                </Button>
              ) : null}
              {selected.canManage && (selected.status === 'open' || selected.status === 'task_created') ? (
                <Button color="inherit" disabled={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>
                  Cancel ticket
                </Button>
              ) : null}
              {selected.canDelete ? (
                <Button color="error" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                  Delete ticket
                </Button>
              ) : null}
              <Button onClick={() => setSelected(null)}>Done</Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
};

export default TicketsPage;
